import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  DIRECTED_CAST_DESCRIPTION_MAX_LENGTH,
  DIRECTED_CAST_LABEL_MAX_LENGTH,
  DIRECTED_CAST_PICTURES_PER_VIDEO,
  type DirectedCastRole,
} from 'fym-shared/src/music24/directedVideoApi';
import { TypedMemo } from '../../common/types';
import { ConfirmDialog } from '../../common/views/modals/confirmDialog/ConfirmDialog';
import { useDirectedVideoContext } from '../DirectedVideoContext';
import { castMessages as messages } from '../messages/castMessages';
import { CastRoles } from './CastRoles';
import { castErrorMessage } from './castErrorMessages';
import { useCastPictureRun } from './useCastPictureRun';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the directed video; each part uses its own slice of it */
import styles from '../DirectedVideo.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The fields that the dialog opens with: a subject of the story that has no picture yet, or the
 * words of a failed picture ("Change the words", which then replaces that failed picture).
 */
export interface CastPictureInitial {
  role: DirectedCastRole;
  label: string;
  description: string;
  /** The failed pending picture that the new picture replaces. */
  replacesPictureId?: string;
}

interface CastPictureDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Fills the fields when the dialog opens (the story prompt and the failed card of the cast step). */
  initial?: CastPictureInitial | null;
}

/**
 * "Make a picture for the cast" (design b2): a role, a label and a description. The AI makes a
 * reference photo (`POST .../cast/picture`). The dialog closes as soon as the picture starts (owner
 * report 2026-10-20): the cast card shows the picture while the AI makes it, and a failed picture
 * offers "Try again" and "Change the words" there. 12 pictures per directed video.
 */
const CastPictureDialogComponent: React.FC<CastPictureDialogProps> = ({ isOpen, onClose, initial = null }) => {
  const intl = useIntl();
  const { video } = useDirectedVideoContext();
  const [role, setRole] = useState<DirectedCastRole>('object');
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const pictureRun = useCastPictureRun();
  const { reset } = pictureRun;
  const left = video.castPicturesLeft;

  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  useEffect(() => {
    if (isOpen && initial !== null) {
      setRole(initial.role);
      setLabel(initial.label.slice(0, DIRECTED_CAST_LABEL_MAX_LENGTH));
      setDescription(initial.description.slice(0, DIRECTED_CAST_DESCRIPTION_MAX_LENGTH));
    }
  }, [initial, isOpen]);

  const replacesPictureId = initial?.replacesPictureId;
  const request = {
    role,
    label: label.trim(),
    description: description.trim(),
    ...(replacesPictureId === undefined ? {} : { replacesPictureId }),
  };
  const canMake = request.description !== '' && left > 0 && !pictureRun.isSending;

  const make = async () => {
    if (await pictureRun.start(request)) {
      onClose();
    }
  };

  return (
    <ConfirmDialog
      isOpen={isOpen}
      tone="neutral"
      title={intl.formatMessage(messages.dialogTitle)}
      description={intl.formatMessage(messages.dialogText)}
      confirmLabel={intl.formatMessage(messages.dialogMake)}
      cancelLabel={intl.formatMessage(messages.dialogClose)}
      isBusy={pictureRun.isSending}
      isConfirmDisabled={!canMake}
      error={pictureRun.error === null ? undefined : intl.formatMessage(castErrorMessage(pictureRun.error))}
      onConfirm={() => {
        make().catch(console.error);
      }}
      onCancel={onClose}
      testId="cast-picture-dialog"
    >
      <div className={styles.form}>
        <span>{intl.formatMessage(messages.dialogRole)}</span>
        <CastRoles value={role} label={label} disabled={pictureRun.isSending} onChange={setRole} />
        <span>{intl.formatMessage(messages.dialogLabel)}</span>
        <input
          className={styles.textInput}
          value={label}
          maxLength={DIRECTED_CAST_LABEL_MAX_LENGTH}
          disabled={pictureRun.isSending}
          aria-label={intl.formatMessage(messages.dialogLabel)}
          onChange={(event) => setLabel(event.target.value)}
        />
      </div>
      <textarea
        className={`${styles.textInput} ${styles.textArea}`}
        value={description}
        maxLength={DIRECTED_CAST_DESCRIPTION_MAX_LENGTH}
        disabled={pictureRun.isSending}
        placeholder={intl.formatMessage(messages.dialogDescriptionPlaceholder)}
        aria-label={intl.formatMessage(messages.dialogDescription)}
        onChange={(event) => setDescription(event.target.value)}
      />
      <p className={`${styles.count} mt-2`}>
        {left <= 0
          ? intl.formatMessage(messages.noPicturesLeft)
          : intl.formatMessage(messages.dialogLeft, { left, total: DIRECTED_CAST_PICTURES_PER_VIDEO })}
      </p>
    </ConfirmDialog>
  );
};

export const CastPictureDialog = TypedMemo(CastPictureDialogComponent);
